import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SEO } from '../components/SEO';
import PublicNav from '../components/PublicNav';

/**
 * Legal Page Component
 *
 * Public-facing legal page with Privacy Policy, Terms of Service,
 * Cookie Policy, and Licenses sections. Each section has an anchor ID
 * for deep linking from footer links across the site.
 */
export function Legal(): React.ReactElement {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Legal"
        description="Nivoon legal information including Privacy Policy, Terms of Service, Cookie Policy, and Licenses."
        path="/legal"
      />

      <PublicNav />

      {/* Hero Header */}
      <section className="pt-20 pb-12 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">Legal</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
            Our commitment to transparency, privacy, and responsible use.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="#privacy" className="px-4 py-2 bg-white/10 text-white text-sm rounded-full hover:bg-white/20 transition-colors border border-white/20">
              Privacy Policy
            </a>
            <a href="#terms" className="px-4 py-2 bg-white/10 text-white text-sm rounded-full hover:bg-white/20 transition-colors border border-white/20">
              Terms of Service
            </a>
            <a href="#cookie-policy" className="px-4 py-2 bg-white/10 text-white text-sm rounded-full hover:bg-white/20 transition-colors border border-white/20">
              Cookie Policy
            </a>
            <a href="#licenses" className="px-4 py-2 bg-white/10 text-white text-sm rounded-full hover:bg-white/20 transition-colors border border-white/20">
              Licenses
            </a>
          </div>
        </div>
      </section>

      {/* Legal Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Privacy Policy */}
          <div id="privacy" className="scroll-mt-24 mb-20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Privacy Policy</h2>
            </div>
            <p className="text-sm text-gray-500 mb-8">Last updated: March 1, 2026</p>

            <div className="prose prose-gray max-w-none space-y-6">
              <p className="text-gray-600 leading-relaxed">
                Nivoon, Inc. ("Nivoon," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform and services.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">1. Information We Collect</h3>
              <p className="text-gray-600 leading-relaxed">We collect information you provide directly when creating an account, including your name, email address, company name, and role. When you use the platform, we also collect usage data such as pages visited, features used, browser type, IP address, and device information.</p>
              <p className="text-gray-600 leading-relaxed">For organizations using our platform, we collect project data, requirements, comments, and other content created by your team. This data belongs to your organization and is isolated from other tenants in our multi-tenant architecture.</p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">2. How We Use Your Information</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>To provide, operate, and maintain the Nivoon platform</li>
                <li>To authenticate users and enforce role-based access controls</li>
                <li>To process AI-powered features (e.g., requirements generation, risk analysis) using third-party AI providers</li>
                <li>To send transactional emails such as account verification, password resets, and notifications</li>
                <li>To monitor and improve platform performance, security, and reliability</li>
                <li>To respond to support requests and communicate with you about your account</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">3. Data Storage and Security</h3>
              <p className="text-gray-600 leading-relaxed">Your data is stored on Microsoft Azure infrastructure in the United States. We implement industry-standard security measures including AES-256 encryption for sensitive credentials, JWT-based authentication, parameterized database queries to prevent SQL injection, and comprehensive audit logging. All data is isolated by company through our multi-tenant architecture.</p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">4. Third-Party Services</h3>
              <p className="text-gray-600 leading-relaxed">We use the following third-party services to operate our platform:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li><strong>OpenAI</strong> — Powers AI features. Project context (requirements, descriptions) is sent to OpenAI's API for processing. OpenAI does not use this data for training.</li>
                <li><strong>Postmark</strong> — Handles transactional email delivery (account verification, notifications).</li>
                <li><strong>Microsoft Azure</strong> — Cloud hosting and database infrastructure.</li>
                <li><strong>Atlassian (JIRA)</strong> — Optional integration. Credentials are encrypted at rest using AES-256-GCM.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">5. Your Rights</h3>
              <p className="text-gray-600 leading-relaxed">Depending on your jurisdiction, you may have the right to access, correct, delete, or export your personal data. You may also have the right to restrict or object to certain processing activities. To exercise any of these rights, contact us at <a href="mailto:info@nivoon.com" className="text-indigo-600 hover:text-indigo-700">info@nivoon.com</a>.</p>
              <p className="text-gray-600 leading-relaxed">For users in the European Economic Area (EEA), we process personal data on the basis of legitimate interest (operating our platform), contractual necessity (providing services you've signed up for), and consent (where applicable).</p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">6. Data Retention</h3>
              <p className="text-gray-600 leading-relaxed">We retain your account data for as long as your account is active. If you or your organization closes an account, we will delete your personal data within 90 days, except where retention is required by law or for legitimate business purposes (e.g., audit logs, billing records).</p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">7. Contact</h3>
              <p className="text-gray-600 leading-relaxed">If you have questions about this Privacy Policy, contact us at <a href="mailto:info@nivoon.com" className="text-indigo-600 hover:text-indigo-700">info@nivoon.com</a>.</p>
            </div>
          </div>

          <hr className="border-gray-200 mb-20" />

          {/* Terms of Service */}
          <div id="terms" className="scroll-mt-24 mb-20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Terms of Service</h2>
            </div>
            <p className="text-sm text-gray-500 mb-8">Last updated: March 1, 2026</p>

            <div className="prose prose-gray max-w-none space-y-6">
              <p className="text-gray-600 leading-relaxed">
                These Terms of Service ("Terms") govern your access to and use of the Nivoon platform. By creating an account or using our services, you agree to be bound by these Terms.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">1. Account Terms</h3>
              <p className="text-gray-600 leading-relaxed">You must provide a valid email address and accurate information to create an account. You are responsible for maintaining the security of your account credentials. You must verify your email address before accessing the platform. One person or organization may not maintain more than one free account.</p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">2. Acceptable Use</h3>
              <p className="text-gray-600 leading-relaxed">You agree to use Nivoon only for lawful purposes and in accordance with these Terms. You must not:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Use the platform to store or transmit malicious code, or to attack or compromise other systems</li>
                <li>Attempt to gain unauthorized access to other users' accounts or data</li>
                <li>Reverse engineer, decompile, or disassemble any part of the platform</li>
                <li>Use automated scripts to access the platform in a way that exceeds reasonable usage</li>
                <li>Resell, sublicense, or redistribute access to the platform without written permission</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">3. Intellectual Property</h3>
              <p className="text-gray-600 leading-relaxed">Nivoon and its licensors retain all rights, title, and interest in the platform, including all software, designs, trademarks, and documentation. You retain ownership of all content you create on the platform (requirements, projects, comments, etc.). By using the platform, you grant Nivoon a limited license to host, process, and display your content solely for the purpose of providing the service.</p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">4. Service Availability</h3>
              <p className="text-gray-600 leading-relaxed">We strive to maintain high availability but do not guarantee uninterrupted access. We may perform scheduled maintenance or updates that temporarily affect availability. We will make reasonable efforts to notify you of planned downtime in advance.</p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">5. Subscription and Billing</h3>
              <p className="text-gray-600 leading-relaxed">Paid plans are billed on a per-user basis at the frequency you select (monthly or annual). All fees are non-refundable except as required by law. We reserve the right to change pricing with 30 days' notice. Free trial periods do not require payment information.</p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">6. Limitation of Liability</h3>
              <p className="text-gray-600 leading-relaxed">TO THE MAXIMUM EXTENT PERMITTED BY LAW, NIVOON SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUE, WHETHER INCURRED DIRECTLY OR INDIRECTLY. OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM THESE TERMS SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.</p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">7. Termination</h3>
              <p className="text-gray-600 leading-relaxed">You may close your account at any time. We may suspend or terminate your access if you violate these Terms, fail to pay applicable fees, or if we are required to do so by law. Upon termination, your right to use the platform ceases immediately. We will retain your data for 90 days after termination to allow for data export, after which it will be deleted.</p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">8. Changes to Terms</h3>
              <p className="text-gray-600 leading-relaxed">We may update these Terms from time to time. We will notify you of material changes via email or through the platform. Continued use of the platform after changes take effect constitutes acceptance of the revised Terms.</p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">9. Governing Law</h3>
              <p className="text-gray-600 leading-relaxed">These Terms are governed by the laws of the State of Minnesota, United States, without regard to conflict of law provisions. Any disputes arising from these Terms shall be resolved in the state or federal courts located in Hennepin County, Minnesota.</p>
            </div>
          </div>

          <hr className="border-gray-200 mb-20" />

          {/* Cookie Policy */}
          <div id="cookie-policy" className="scroll-mt-24 mb-20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Cookie Policy</h2>
            </div>
            <p className="text-sm text-gray-500 mb-8">Last updated: March 1, 2026</p>

            <div className="prose prose-gray max-w-none space-y-6">
              <p className="text-gray-600 leading-relaxed">
                This Cookie Policy explains how Nivoon uses cookies and similar technologies when you visit our website and use our platform.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">1. What Are Cookies</h3>
              <p className="text-gray-600 leading-relaxed">Cookies are small text files stored on your device when you visit a website. They help websites remember your preferences, keep you logged in, and understand how you use the site.</p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">2. Cookies We Use</h3>

              <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">Purpose</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 font-medium text-gray-900">Essential</td>
                      <td className="px-4 py-3 text-gray-600">Authentication (JWT tokens), session management, and CSRF protection. Required for the platform to function.</td>
                      <td className="px-4 py-3 text-gray-600">24 hours</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-gray-900">Preferences</td>
                      <td className="px-4 py-3 text-gray-600">Remembering your UI preferences such as sidebar state, theme selection, and collapsed sections.</td>
                      <td className="px-4 py-3 text-gray-600">Persistent (localStorage)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-gray-900">Analytics</td>
                      <td className="px-4 py-3 text-gray-600">Understanding how visitors interact with our public pages (page visits, referrer, screen resolution). Used to improve the product.</td>
                      <td className="px-4 py-3 text-gray-600">Session</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">3. Third-Party Cookies</h3>
              <p className="text-gray-600 leading-relaxed">We do not use third-party advertising cookies. Our third-party integrations (OpenAI, Postmark) do not set cookies on your device. If you use the optional JIRA integration, Atlassian may set cookies according to their own cookie policy when you authenticate.</p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">4. Managing Cookies</h3>
              <p className="text-gray-600 leading-relaxed">You can control cookies through your browser settings. Most browsers allow you to block or delete cookies. However, disabling essential cookies will prevent you from logging in and using the platform. Clearing localStorage will reset your UI preferences but will not affect your account data.</p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">5. Updates</h3>
              <p className="text-gray-600 leading-relaxed">We may update this Cookie Policy as we add new features or integrations. Changes will be reflected on this page with an updated "Last updated" date.</p>
            </div>
          </div>

          <hr className="border-gray-200 mb-20" />

          {/* Licenses */}
          <div id="licenses" className="scroll-mt-24 mb-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Licenses</h2>
            </div>
            <p className="text-sm text-gray-500 mb-8">Last updated: March 1, 2026</p>

            <div className="prose prose-gray max-w-none space-y-6">
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">1. Nivoon Platform</h3>
              <p className="text-gray-600 leading-relaxed">The Nivoon platform, including all proprietary source code, designs, documentation, and branding, is the intellectual property of Nivoon, Inc. All rights reserved. Access to the platform is provided under a subscription license as described in our Terms of Service.</p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">2. Open-Source Software</h3>
              <p className="text-gray-600 leading-relaxed">Nivoon is built with the help of open-source software. We are grateful to the open-source community and acknowledge the following key libraries and their respective licenses:</p>

              <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">Library</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">License</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-gray-900">React</td>
                      <td className="px-4 py-3 text-gray-600">MIT License</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-900">Express</td>
                      <td className="px-4 py-3 text-gray-600">MIT License</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-900">Vite</td>
                      <td className="px-4 py-3 text-gray-600">MIT License</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-900">TypeScript</td>
                      <td className="px-4 py-3 text-gray-600">Apache License 2.0</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-900">Tailwind CSS</td>
                      <td className="px-4 py-3 text-gray-600">MIT License</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-900">Nivo Charts</td>
                      <td className="px-4 py-3 text-gray-600">MIT License</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-900">tldraw</td>
                      <td className="px-4 py-3 text-gray-600">Apache License 2.0</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-900">Leaflet</td>
                      <td className="px-4 py-3 text-gray-600">BSD 2-Clause License</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-900">jsPDF</td>
                      <td className="px-4 py-3 text-gray-600">MIT License</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-900">@hello-pangea/dnd</td>
                      <td className="px-4 py-3 text-gray-600">Apache License 2.0</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-900">@dnd-kit</td>
                      <td className="px-4 py-3 text-gray-600">MIT License</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-900">jsonwebtoken</td>
                      <td className="px-4 py-3 text-gray-600">MIT License</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-gray-900">mssql (node-mssql)</td>
                      <td className="px-4 py-3 text-gray-600">MIT License</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-600 leading-relaxed mt-4">This is not an exhaustive list. A complete list of dependencies and their licenses can be found in the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm">package.json</code> files of the project. All open-source libraries are used in compliance with their respective license terms.</p>

              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-3">3. Your Content</h3>
              <p className="text-gray-600 leading-relaxed">Content you create on the Nivoon platform (requirements, projects, comments, documents, whiteboard drawings, etc.) remains your intellectual property. Nivoon does not claim ownership of user-generated content. See our Terms of Service for details on the limited license you grant us to host and display your content.</p>
            </div>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li><a href="/features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="/pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Security</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Roadmap</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="/investor-relations" className="hover:text-white transition-colors">Investor Relations</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Resources</h3>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Community</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><a href="#privacy" className="hover:text-white transition-colors">Privacy</a></li>
                <li><a href="#terms" className="hover:text-white transition-colors">Terms</a></li>
                <li><a href="#cookie-policy" className="hover:text-white transition-colors">Cookie Policy</a></li>
                <li><a href="#licenses" className="hover:text-white transition-colors">Licenses</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <a href="/">
                <img src="/logo1.png" alt="Nivoon" className="h-8 opacity-70" />
              </a>
            </div>
            <p className="text-sm text-gray-400">
              &copy; 2026 Nivoon. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Legal;
